
import Vue from "vue";
import {Address, AddressType, Contact, contactApi, ContactSearchRow, createContactInfos,} from "@/api/contacts";
import {Dossier} from "@/api/flame";
import ContactAddress from "@/views/contacts/ContactAddress.vue";
import {ContactRelationResult,} from "@/api/contactRelation";
import {findConfirmDialogRef} from "@/utils/RefUtils";

export interface DeliveryAddressFormInputDTO {
  dossier: Dossier;
  contact: Contact;
  resellerContacts: ContactRelationResult[];
}

export default Vue.extend({
  components: {
    ContactAddress
  },
  props: {
    deliveryAddressFormInput: {
      type: Object as () => DeliveryAddressFormInputDTO,
      required: true,
    },
    readonly: {
      type: Boolean,
    },
  },
  data() {
    return {
      valid: true,
      isWorking: false,
      deliveryAddressRef: this.deliveryAddressFormInput.dossier.deliveryAddressRef,
      deliveryAddress: null as Address | null,
      contactFromDeliveryAddress: null as Contact | null,
      newDeliveryAddressContactRef: null as string | null,
      dossierId: null as string | null,
    };
  },
  computed: {
    isDeliveryAddressNotEqualBillingAddress(): boolean {
      const cond1 = this.deliveryAddressFormInput.dossier.deliveryAddressRef !== this.deliveryAddressFormInput.dossier.billingAddressRef && this.newDeliveryAddressContactRef === null;
      const cond2 = this.newDeliveryAddressContactRef !== null;
      return cond1 || cond2;
    },
    addresses(): { value: string; text: string }[] {
      const addresses: { value: string; text: string }[] = [];
      if (this.deliveryAddressFormInput.resellerContacts && this.deliveryAddressFormInput.resellerContacts.length > 0) {
        this.deliveryAddressFormInput.resellerContacts.forEach(
            (resellerContact: ContactRelationResult) => {
              resellerContact.withContact.addresses.forEach((address) => {
                addresses.push({
                  value: address.id ?? "",
                  text:
                      createContactInfos(address.sharedByContactPersonRef ?? null, resellerContact.withContact) + " - " +
                      (address.street ? address.street : "keine Strasse") +
                      ", " +
                      (address.city ? address.city : "kein Ort") +
                      " " +
                      (address.zipCode ? address.zipCode : "keine PLZ"),
                });
              });
            }
        );
      }

      if (this.deliveryAddressFormInput.contact && this.deliveryAddressFormInput.contact.addresses) {
        this.deliveryAddressFormInput.contact.addresses.forEach((address) => {
          addresses.push({
            value: address.id ?? "",
            text:
                createContactInfos(address.sharedByContactPersonRef ?? null, this.deliveryAddressFormInput.contact) + " - " +
                (address.street ? address.street : "keine Strasse") +
                ", " +
                (address.city ? address.city : "kein Ort") +
                " " +
                (address.zipCode ? address.zipCode : "keine PLZ"),
          });
        });
      }

      return addresses;
    },
  },
  watch: {
    deliveryAddressFormInput: {
      async handler() {
        if (this.deliveryAddress == null || this.deliveryAddressFormInput.dossier.id !== this.dossierId) {
          this.resetData()
          if (this.deliveryAddressFormInput.dossier) {
            this.deliveryAddressRef = this.deliveryAddressFormInput.dossier.deliveryAddressRef;
            if (this.deliveryAddressRef !== null && this.deliveryAddressRef !== undefined) {
              const contactId = this.findContactIdForAddressRef(this.deliveryAddressRef);
              if (contactId) {
                this.contactFromDeliveryAddress = await contactApi.getById(contactId)
                this.deliveryAddress = await contactApi.getAddressById(
                    this.findContactIdForAddressRef(this.deliveryAddressRef) ??
                    "",
                    this.deliveryAddressRef
                );
              }
            }
          }
        }
      },
      immediate: true,
    },
  },
  methods: {

    resetData() {
      this.deliveryAddressRef = "";
      this.deliveryAddress = null;
      this.newDeliveryAddressContactRef = null;
      this.dossierId = this.deliveryAddressFormInput.dossier.id;
      this.contactFromDeliveryAddress = this.deliveryAddressFormInput.contact;
    },
    async setDeliveryAddress() {
      if (this.deliveryAddressRef) {
        this.deliveryAddress = await contactApi.getAddressById(
            this.findContactIdForAddressRef(this.deliveryAddressRef) ??
            "",
            this.deliveryAddressRef
        );
      }
      await this.updateDossier();
    },
    async updateDossier() {
      if (this.deliveryAddressRef) {
        this.$emit("deliveryAddressChanged", this.deliveryAddressRef);
      } else if (this.deliveryAddressFormInput.dossier.deliveryAddressRef !== null) {
        this.deliveryAddress = null;
        this.$emit("deliveryAddressChanged", null);
      }
    },
    findContactIdForAddressRef(addressRef: string): string | null {
      if (this.deliveryAddressFormInput.resellerContacts && this.deliveryAddressFormInput.resellerContacts.length > 0) {
        let byReseller = null;
        this.deliveryAddressFormInput.resellerContacts.forEach((reseller: ContactRelationResult) => {
          if (
              reseller.withContact.addresses.find((a) => a.id === addressRef)?.id
          ) {
            byReseller = reseller.withContact.id;
          }
        });
        if (byReseller) {
          return byReseller;
        }
      }
      if (this.deliveryAddressFormInput.contact) {
        return this.deliveryAddressFormInput.contact.id;
      }
      return null;
    },
    changedDeliveryAddress(address: Address) {
      this.deliveryAddress = address;
      this.updateDossier();
      this.newDeliveryAddressContactRef = null;
    },
    async saveDeliveryAddress() {
      let contactName = this.deliveryAddressFormInput.contact.fullName;
      let contactId = this.deliveryAddressFormInput.contact.id;
      if (this.deliveryAddressFormInput.resellerContacts.length > 0 && this.newDeliveryAddressContactRef) {
        contactName = this.deliveryAddressFormInput.resellerContacts.find(contact => contact.id = this.newDeliveryAddressContactRef)?.withContact?.fullName;
        contactId = this.deliveryAddressFormInput.resellerContacts.find(contact => contact.id = this.newDeliveryAddressContactRef)?.withContact?.id ?? "";
      }

      const res = await (findConfirmDialogRef(this) as any).open(
          this.$t("Achtung") as string,
          this.$t(
              `Lieferadresse aktualisieren? Adresse wird im Kontakt ${contactName} ebenfalls aktualisiert`
          ) as string,
          {
            textColor: "red",
            noconfirm: false,
          }
      );
      if (res) {
        if (this.deliveryAddress && this.deliveryAddress.id && contactId) {
          const updatedAddress =  await contactApi.editAddress(
              contactId,
              this.deliveryAddress.id,
              this.deliveryAddress
          );
          this.deliveryAddress = {
            ...updatedAddress,
          };
        } else if (this.deliveryAddress && contactId) {
          this.deliveryAddressRef = await contactApi.addNewAddress(
              contactId,
              this.deliveryAddress
          );
          this.deliveryAddress = {
            ...this.deliveryAddress,
            id: this.deliveryAddressRef,
          };
        }
        await this.updateDossier();
      }
    },
    async addNewDeliveryAddressFor(contact: Contact | ContactSearchRow) {
      this.deliveryAddress = {addressType: AddressType.HOME} as Address;
      this.newDeliveryAddressContactRef = contact.id;
    },
  },
});
